import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { TRANSLATIONS } from '../constants';
import { Trash2, Plus, Minus, ArrowRight, ShoppingBag } from 'lucide-react';
import { updateMetaTags } from '../utils/seo';
import { SEO_CONFIG, getFullUrl } from '../config/seo.config';

export const Cart = () => {
  const { cart, removeFromCart, updateQuantity, language } = useStore();
  const navigate = useNavigate();
  const t = TRANSLATIONS;

  useEffect(() => {
    updateMetaTags({
      title: `${t.cart[language]} - ${SEO_CONFIG.storeName}`,
      description: 'Review your shopping cart and proceed to checkout',
      canonical: getFullUrl('/cart'),
    });
  }, [language, t]);

  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  if (cart.length === 0) {
    return (
      <main className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
           <ShoppingBag className="w-10 h-10 text-gray-400" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">{t.emptyCart[language]}</h2>
        <Link to="/" className="text-emerald-600 hover:text-emerald-700 font-medium mt-4">
           {language === 'en' ? 'Continue Shopping' : 'Kontynuuj zakupy'}
        </Link>
      </main>
    );
  }

  return (
    <main className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 md:mb-8">{t.cart[language]}</h1>
      
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {cart.map((item) => {
          const minQty = item.minQuantity || 6;
          return (
          <div key={item.id} className="p-4 md:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 border-b border-gray-100 last:border-0">
            <div className="flex items-center gap-4 w-full sm:w-auto">
                <img src={item.image} alt={item.name} className="w-20 h-20 md:w-20 md:h-20 object-cover rounded-lg bg-gray-50 flex-shrink-0" />
                
                {/* Mobile Title View (hidden on Desktop) */}
                <div className="flex-1 sm:hidden">
                    <h3 className="font-semibold text-gray-900 line-clamp-2">{item.name}</h3>
                    <p className="text-gray-500 text-sm mt-1">{item.price.toFixed(2)} zł</p>
                </div>
            </div>
            
            <div className="flex-1 hidden sm:block">
              <h3 className="font-semibold text-gray-900">{item.name}</h3>
              <p className="text-gray-500 text-sm">{item.price.toFixed(2)} zł</p>
              {item.quantity === minQty && (
                  <p className="text-xs text-orange-500 mt-1">Min. quantity: {minQty}</p>
              )}
            </div>

            {/* Mobile Quantity Control Row */}
            <div className="flex items-center justify-between w-full sm:w-auto mt-2 sm:mt-0">
                {/* Mobile Min Quantity Alert */}
                <div className="sm:hidden text-xs text-orange-500 mr-2">
                    {item.quantity === minQty && `Min: ${minQty}`}
                </div>

                <div className="flex items-center gap-3">
                    <button 
                        onClick={() => {
                            if (item.quantity <= minQty) {
                                removeFromCart(item.id);
                            } else {
                                updateQuantity(item.id, item.quantity - minQty);
                            }
                        }}
                        className={`p-1 rounded-md text-gray-500 ${item.quantity <= minQty ? 'hover:bg-red-50 hover:text-red-500' : 'hover:bg-gray-100'}`}
                        title={item.quantity <= minQty ? "Remove item" : "Decrease quantity"}
                    >
                        {item.quantity <= minQty ? <Trash2 className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                    </button>
                    <span className="w-8 text-center font-medium">{item.quantity}</span>
                    <button 
                        onClick={() => updateQuantity(item.id, item.quantity + minQty)}
                        className="p-1 rounded-md hover:bg-gray-100 text-gray-500"
                    >
                        <Plus className="w-4 h-4" />
                    </button>
                </div>
                
                <div className="flex items-center gap-4 sm:ml-6">
                    <p className="font-bold text-gray-900 sm:min-w-[80px] text-right">
                        {(item.price * item.quantity).toFixed(2)} zł
                    </p>
                    <button 
                        onClick={() => removeFromCart(item.id)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove"
                    >
                        <Trash2 className="w-5 h-5" />
                    </button>
                </div>
            </div>
          </div>
        );
        })}
      </div>

      <div className="mt-8 flex flex-col md:flex-row justify-between items-center md:items-end gap-6 pb-20 md:pb-0">
        <Link to="/categories" className="text-gray-500 hover:text-gray-900 font-medium order-2 md:order-1">
          &larr; {language === 'en' ? 'Continue Shopping' : 'Wróć do sklepu'}
        </Link>
        
        <div className="flex flex-col items-center md:items-end gap-4 w-full md:w-auto order-1 md:order-2">
          <div className="text-2xl font-bold text-gray-900">
            <span className="text-lg font-normal text-gray-500 mr-2">{t.total[language]}:</span>
            {total.toFixed(2)} zł
          </div>
          <button 
            onClick={() => navigate('/checkout')}
            className="w-full md:w-auto bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-8 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-emerald-200"
          >
            {t.checkout[language]}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </main>
  );
};